import { useState, useMemo, useEffect, useRef, useContext } from "react";
import SignIn from "src/components/SignIn.js";
import CenterDialog from "./CenterDialog.js";
import { Typography, Avatar, Box } from "@mui/material";
import { useProfileImage } from "src/utils/useProfileImage.js";
import { useGetCalendarsByHandle } from "src/services/index.js";
import { CalendarToday } from "@mui/icons-material";
import LazyImageComponent from "../LazyImageComponent.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { throttle } from "lodash";
import { customEvent } from "src/utils/gtag.js";
import StanzaLogo from "src/assets/svg/stanza-logo.js";

const SCROLL_TRIGGER_PERCENTAGE = 2;

/**
 * Head content for SignInDialog, supports both calendar and custom UI.
 */
const SignInHeadContent = ({ calendar, profileImage, mainTitle, subtitle }) => {
  // Calendar UI
  if (calendar) {
    return (
      <>
        <LazyImageComponent
          sx={{
            mx: "auto",
            mt: 0.5,
            borderRadius: "999px",
            width: "50px",
            height: "50px",
          }}
          showOnError
        >
          <Avatar
            src={profileImage}
            sx={{
              mx: "auto",
              mt: 0.5,
              width: "50px",
              height: "50px",
              placeContent: "center",
            }}
          >
            <CalendarToday />
          </Avatar>
        </LazyImageComponent>
        <Typography variant={"h5"} sx={{ mt: 1, mb: 0.5 }}>
          Discover more from
        </Typography>
        <Typography variant={"h2"} sx={{ mb: 2 }}>
          {calendar.name}
        </Typography>
        <Typography variant={"subtitle2"} sx={{ mb: 1 }} color="text.secondary">
          Get the latest events in your calendar.
        </Typography>
        <Typography variant={"subtitle2"} sx={{ mb: 3 }} color="text.secondary">
          {calendar?.subscribers >= 100
            ? `${
                calendar.subscribers >= 1000
                  ? new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(calendar.subscribers)
                  : calendar.subscribers
              } subscribers`
            : ""}
        </Typography>
      </>
    );
  }

  // Custom UI (for Stanza logo, custom texts, etc)
  return (
    <>
      <Box
        sx={{
          mx: "auto",
          mt: 0.5,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 60,
          height: 60,
          borderRadius: "50%",
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <StanzaLogo
          style={{
            width: 45,
            height: 45,
            display: "block",
          }}
          aria-label="Stanza logo"
        />
      </Box>
      {mainTitle && (
        <Typography variant="h2" sx={{ mt: 2, mb: 2, textAlign: "center" }}>
          {mainTitle}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="h5"
          sx={{ mb: 3, textAlign: "center" }}
          color="text.secondary"
        >
          {subtitle}
        </Typography>
      )}
    </>
  );
};

const SignInDialog = ({
  open,
  handleClose,
  calendar = null,
  openOnScroll = false,
  onSignIn = () => {},
  headContent = null, // custom: mainTitle, subtitle
  hideCloseButton,
  disableClickOutside,
}) => {
  const handle = calendar?.handle;
  const { data: dataCalendars } = useGetCalendarsByHandle(
    { handle },
    { enabled: !!handle }
  );
  const profileImage = useProfileImage(
    useMemo(() => dataCalendars?.creator, [dataCalendars])
  );
  const { user } = useContext(AuthContext);
  const [isScrolledEnough, setIsScrolledEnough] = useState(false);

  const dialogId = `signin-dialog${openOnScroll ? "-scrollable" : ""}`;
  const dialogRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    function trackShowSignUpModal() {
      if (open && sessionStorage.getItem("seenSignUpModal") !== "true") {
        if (openOnScroll && !isScrolledEnough) return; // is not visible
        customEvent({
          name: "signup_modal_shown",
          category: "Authorization",
          label: "Sign up modal shown",
          action: "view",
        });
        sessionStorage.setItem("seenSignUpModal", "true");
      }
    }
    trackShowSignUpModal();
  }, [isScrolledEnough, openOnScroll, open]);

  const openOnScrollProps = {
    sx: {
      height: "100vh",
      minHeight: "100vh",
      willChange: "transform",
      transform: isScrolledEnough ? "translateY(0)" : "translateY(100vh)",
      // delay strategy with 100ms more than paper and backdrop transition
      transition: !isScrolledEnough ? "transform 0s linear 0.5s" : "",
    },
    openOnScroll,
    PaperProps: {
      ref: dialogRef,
      sx: {
        position: "fixed",
        willChange: "transform",
        pointerEvents: "auto",
        margin: 0,
        width: "100%",
        maxWidth: "400px",
        transform: isScrolledEnough ? "translateY(0)" : "translateY(100vh)",
        transition: "transform 0.4s ease, opacity 0.4s ease",
      },
    },
    BackdropProps: {
      ref: backdropRef,
      style: {
        opacity: isScrolledEnough ? 1 : 0,
        willChange: "transform",
        transform: isScrolledEnough ? "translateY(0)" : "translateY(100vh)",
        transition: "opacity 0.4s ease",
      },
    },
    disableScrollLock: true,
  };

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset;
          const windowHeight = window.innerHeight;
          const scrollPercentage = scrollTop / windowHeight;

          if (
            dialogRef.current &&
            backdropRef.current &&
            !document.getElementById("signin-dialog")
          ) {
            if (scrollPercentage >= SCROLL_TRIGGER_PERCENTAGE) {
              setIsScrolledEnough(true);
            } else {
              setIsScrolledEnough(false);
            }
          }
        });
      }, 30),
    []
  );

  const handleCloseDialog = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    handleClose();
  };

  useEffect(() => {
    if (openOnScroll && open) {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [openOnScroll, open]);

  useEffect(() => {
    if (user && open) {
      handleClose();
      onSignIn();
    }
  }, [Boolean(user)]);

  // Decide which headContent to use
  const headContentProps = calendar
    ? {
        calendar,
        profileImage,
      }
    : headContent;

  return (
    <CenterDialog
      id={dialogId}
      data-testid={dialogId}
      open={open}
      handleClose={
        (openOnScroll && calendar) || disableClickOutside
          ? handleCloseDialog
          : handleClose
      }
      hideCloseButton={!!calendar || hideCloseButton}
      {...(openOnScroll && calendar ? openOnScrollProps : {})}
      sx={{
        "& .MuiPaper-root": {
          width: { xs: "80%", md: "auto" },
        },
        "& .MuiDialogContent-root": { width: { md: "330px" } },
        ...(openOnScroll && calendar ? openOnScrollProps.sx : {}),
      }}
    >
      <SignIn
        dialog
        headContent={
          (headContent || calendar) && (
            <SignInHeadContent {...headContentProps} />
          )
        }
      />
    </CenterDialog>
  );
};

export default SignInDialog;
